import { gql } from '@apollo/client';
import Fragments from './fragments';

const {
    MEDIA_PLAYBACK_FIELDS,
    CORE_SEARCH_FIELD_METADATA,
} = Fragments;

const SEARCH = gql`
    query Search(
        $query: String!
        $page: Int
        $limit: Int
        $feedPreference: FeedPreference
        $languagePreference: LanguagePreference
        $contentPreference: ContentPreference
        $queryType: QueryType = STRUCTURED
    ) {
        search(
            query: $query
            limit: $limit
            page: $page
            feedPreference: $feedPreference
            languagePreference: $languagePreference
            contentPreference: $contentPreference
            queryType: $queryType
        ) {
            plays {
                mediaPlayback {
                    id
                    slug
                    blurb
                    date
                    description
                    title
                    canAddToReel
                    feeds {
                        type
                        duration
                        image {
                            altText
                            templateUrl
                            cuts {
                                width
                                src
                            }
                        }
                        playbacks {
                            name
                            segments
                        }
                    }
                    playInfo {
                        balls
                        strikes
                        outs
                        inning
                        inningHalf
                        pitchSpeed
                        pitchType
                        exitVelocity
                        hitDistance
                        launchAngle
                        spinRate
                        scoreDifferential
                        gamePk
                        runners {
                            first
                            second
                            third
                        }
                        teams {
                            away {
                                name
                                shortName
                                triCode
                            }
                            home {
                                name
                                shortName
                                triCode
                            }
                            batting {
                                name
                                shortName
                                triCode
                            }
                            pitching {
                                name
                                shortName
                                triCode
                            }
                        }
                        players {
                            pitcher {
                                id
                                name
                                lastName
                                playerHand
                            }
                            batter {
                                id
                                name
                                lastName
                                playerHand
                            }
                        }
                    }
                    isInReel @client
                    keywordsDisplay {
                        slug
                        displayName
                    }
                }
            }
            total
        }
    }
`;

const SEARCH_WITH_PLAYBACK = gql`
    query Search(
        $query: String!
        $page: Int
        $limit: Int
        $feedPreference: FeedPreference
        $languagePreference: LanguagePreference
        $contentPreference: ContentPreference
        $queryType: QueryType = STRUCTURED
    ) {
        search(
            query: $query
            limit: $limit
            page: $page
            feedPreference: $feedPreference
            languagePreference: $languagePreference
            contentPreference: $contentPreference
            queryType: $queryType
        ) {
            plays {
                mediaPlayback {
                    ...MediaPlaybackFields
                }
            }
            total
        }
    }
    ${MEDIA_PLAYBACK_FIELDS}
`;

const GET_QUERY_INFO = gql`
    query getQueryInfo(
        $queries: [String]!,
        $queryType: QueryType = STRUCTURED
    ) {
        queryInfo(
            queries: $queries,
            queryType: $queryType
        ) {
            query
            valid
            graph
        }
    }
`;

const GET_FIELDS = gql`
    query getFields($ids: [Int], $idsSkipValues: [Int]) {
        withValues: fields(ids: $ids) {
            ...CoreSearchFieldMetadata
            values {
                value
                displayValue
            }
        }
        withoutValues: fields(ids: $idsSkipValues) {
            ...CoreSearchFieldMetadata
        }
    }
    ${CORE_SEARCH_FIELD_METADATA}
`;

const GET_CLIP = gql`
    query clipQuery(
        $ids: [String]
        $languagePreference: LanguagePreference
        $idType: MediaPlaybackIdType
        $userId: String!
        $withUser: Boolean!
    ) {
        mediaPlayback(
            ids: $ids
            languagePreference: $languagePreference
            idType: $idType
        ) {
            ...MediaPlaybackFields
        }
        userInfo(
            userId: $userId
        ) @include(if: $withUser) {
            firstName
            nickName
            userId
        }
    }
    ${MEDIA_PLAYBACK_FIELDS}
`;

const GET_TOPIC = gql`
    query getTopic(
        $topicId: String!
        $languagePreference: LanguagePreference
        $limit: Int
        $page: Int
        $videoSlug: String
        $contentGroup: ContentGroup
        $videoTranslationId: String
        $sessionId: String
    ) {
        topicPlayList(
            topicId: $topicId
            languagePreference: $languagePreference
            limit: $limit
            page: $page
            videoSlug: $videoSlug
            contentGroup: $contentGroup
            videoTranslationId: $videoTranslationId
            sessionId: $sessionId
        ) {
            hasMore
            title
            puid
            modelType
            modelVersion
            mediaPlayback {
                ...MediaPlaybackFields
            }
        }
    }
    ${MEDIA_PLAYBACK_FIELDS}
`;

const GET_SELECTED_FILTERS = gql`
    query getSelectedFilters {
        selectedFilters @client {
            name
            type
            comparator
            fieldId
            displayName
            logicalConjunctionOperator
            values {
                value
                label
            }
        }
    }
`;

const GET_SEARCH_SORT = gql`
    query getSearchSort {
        searchSort @client
    }
`;

const GET_PAGE = gql`
    query getPage {
        page @client
    }
`;

const GET_MODALS = gql`
    query getModals {
        modals @client
    }
`;

const GET_NAV_ITEMS = gql`
    query getNavItems {
        navItems @client {
            text
            link
        }
    }
`;

const GET_IS_EU = gql`
    query getIsEU {
        isEU @client
    }
`;

const GET_TARGET_OFFER = gql`
    query getTargetOffer {
        targetOffer @client
    }
`;

const GET_REEL = gql`
    query getReel {
        reel @client {
            reelTitle
            videoUrl
            thumbnailUrl
            duration
            reelClips {
                mediaId
                clipSlug
                feedType
                thumbnailUrl
                originalDuration
                offsetStartTime
                offsetEndTime
                trimmedDuration
            }
        }
    }
`;

const GET_SHARE_MEDIA_ID = gql`
    query getShareMediaId {
        shareMediaId @client
    }
`;

const GET_REELS = gql`
    query getReels(
        $userId: String!
        $limit: Int
        $page: Int
    ) {
        getReels(
            userId: $userId
            limit: $limit
            page: $page
        ) {
            total
            reels {
                id
                mediaPlayback {
                    ...MediaPlaybackFields
                }
            }
        }
        userInfo(userId: $userId) {
            firstName
            nickName
        }
    }
    ${MEDIA_PLAYBACK_FIELDS}
`;

const GET_REEL_CLIP_TRIMMING_MEDIA_ID = gql`
    query getReelClipTrimmingMediaId {
        trimmingMediaId @client
    }
`;


const VALIDATE_REEL = gql`
    query ValidateReel($reelRequest: ReelInput!) {
        validateReel(reelRequest: $reelRequest) {
            valid
            errors
        }
    }
`;

const GET_USER_DATA = gql`
    query getUserData {
        userData @client(always:true) {
            isLoggedIn
            accessToken
            userId
        }
    }
`;

const GET_NOTIFICATION = gql`
    query getNotification ($id: String!) {
        notification(id: $id) @client(always:true){
            id
            isDismissed
        }
    }
`;

const GET_DELETE_REEL_ID = gql`
    query getDeleteReelId {
        deleteReelId @client
    }
`;

const GET_CONTENT_TAG_DISPLAY_VALUES = gql`
    query getContentTagDisplayValues($contentTags: [String!]) {
        contentTagDisplayValues(contentTags: $contentTags) {
            slug
            displayName
        }
    }
`;

const GET_IS_SORTING = gql`
    query getIsSorting {
        isSorting @client
    }
`;

const GET_HOMEPAGE_MODULES = gql`
    query getHomepageModules(
        $lang: String!
        $dir: String!
    ) {
        homepageModules(
            lang: $lang
            dir: $dir
        ) @rest(type: "HomepageModules", path: "/mlb/snippets/film-room/{args.dir}/{args.lang}") {
            modules
        }
    }
`;

export default {
    SEARCH,
    SEARCH_WITH_PLAYBACK,
    GET_QUERY_INFO,
    GET_FIELDS,
    GET_SELECTED_FILTERS,
    GET_PAGE,
    GET_MODALS,
    GET_CLIP,
    GET_TOPIC,
    GET_IS_EU,
    GET_NAV_ITEMS,
    GET_TARGET_OFFER,
    GET_REEL,
    GET_REELS,
    GET_REEL_CLIP_TRIMMING_MEDIA_ID,
    GET_SHARE_MEDIA_ID,
    VALIDATE_REEL,
    GET_USER_DATA,
    GET_NOTIFICATION,
    GET_DELETE_REEL_ID,
    GET_SEARCH_SORT,
    GET_CONTENT_TAG_DISPLAY_VALUES,
    GET_IS_SORTING,
    GET_HOMEPAGE_MODULES,
};
